import React, { useCallback } from "react";
import { connect } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useModelCodeLib } from "utils/codes/modelCodeLib";
import { getBaseUnits, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Tooltip from "components/Tooltip";

import { actions } from "features/Model/Enclosure/_ducks";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";
import WarningIcon from "assets/images/icons/WarningOrange.svg";
import { isOrgCodeRef } from "utils/codes/refs";

const { setInitCode, updateCodeDrawer, updateCompositeCalc } = actions;

//Note that pony walls use the crawlspace wall construction type, which is identical to a standard wall
const ponyWallTypeValidation = [required, ...getValidation("basementPonyWallType")];
const ponyHeightValidation = getValidation("basementPonyWallHeight");

const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// CrawlspaceWall Type codes found in code library
const crawlWallTypeLibCodesMap = (libCodes, handleEditLibCrawlWallCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("CrawlspaceWall-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditLibCrawlWallCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Crawlspace wall (pony wall) Type codes found in model, filtered by those in library
const crawlWallTypeModelCodesMap = (modelCodes, libCodes, handleEditModelCrawlWallCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "CrawlspaceWall") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "CrawlspaceWall";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("CrawlspaceWall-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelCrawlWallCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const PonyWall = React.memo(
    ({
        accessor,
        change,
        id,
        setInitCode,
        updateCodeDrawer,
        updateCompositeCalc,
        isUpgrade = false,
        formName,
        canBeEditedFromParametric = true,
        isCompact = false,
        selectedUpgrade,
        componentAccessor = accessor,
        isCommunity = false,
        codeRefs = [],
    }) => {
        const { CrawlspaceWall: { codes: libCodes = {} } = {} } = useModelCodeLib() || {};
        const { calculatingRValue = {} } = useStateSelector("enclosure") || {};
        const { currentPackage = {} } = useStateSelector("upgradeLibrary") || {};
        const { parametricAnalysis } = useStateSelector("parametricAnalysis") || {};
        const { community } = useStateSelector("community") || {};
        const model = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                  .map((key) =>
                      Object.entries(ecmInputMap[key])
                          .filter(([k, _]) => k !== "None")
                          .map(([k, v]) => ({ [k]: v }))
                  )
                  .flat()
            : {};

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            model?.modelData?.codes ||
            {};

        const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
        const isEnabled = useStateSelector(`${componentAccessor}.wall.ponyWall.enabled`);
        const depthBelowGrade = useStateSelector(`${componentAccessor}.wall.measurements.depth`) || 0;
        const totalWallHeight = useStateSelector(`${componentAccessor}.wall.measurements.height`) || 0;
        const ponyWallHeight = useStateSelector(`${componentAccessor}.wall.ponyWall.ponyWallHeight`) || 0;
        const expPerimeterValue = useStateSelector(`${accessor}.exposedSurfacePerimeter`);

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType_nomRVal`) || 0;
        const selectedEffRValue = useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType_effRVal`) || 0;
        const compositePonyEffRValue =
            useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType_composite.effRVal`) || 0;
        const ponyWallCodeWarning = useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType_warning`) || "";
        const ponyWallHeightUnits = useStateSelector(`${accessor}.wall.ponyWall.ponyWallHeight_u`);
        const rValUnits = useStateSelector(`${accessor}.wall.ponyWall.ponyWallInsType_u`);
        const { window = {}, door = {} } = useStateSelector(`${accessor}.subcomponents`) || {};
        const { [id]: { ponyWallInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified";

        const ponyWallPlusDepth = ponyWallHeight + depthBelowGrade;
        const ponyHeightWarning =
            parseFloat(ponyWallPlusDepth.toFixed(2)) >= parseFloat(totalWallHeight.toFixed(2))
                ? "ponyHeightWarning"
                : "";

        const ponyWallHeightEquation = useStateSelector(`${accessor}.wall.ponyWall.ponyWallHeight_eqn`);

        const subcompArea = [...Object.values(window), ...Object.values(door)].reduce(
            (sum, curr) => sum + curr?.measurements?.area || 0,
            0
        );

        const ponyWallArea = ponyWallHeight * expPerimeterValue;
        const ponyWallSubCompAreaWarning =
            isEnabled && subcompArea > ponyWallArea && ponyWallArea > 0 ? "ponyWallSubCompAreaWarning" : "";

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_warning`, "");
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_composite`, {});
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_nomRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_effRVal`, modelMatch.nominalRValue || 0);
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libCodes[codeRef] || {};
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_nomRVal`, nominalRValue || 0);
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_effRVal`, nominalRValue || 0);
                    change(`${accessor}.wall.ponyWall.ponyWallInsType_warning`, warningType || "");

                    change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                }
                change(`${accessor}.wall.ponyWallInsType_composite`, {});

                //Redundancy
                change(`model.stashedCodes.CrawlspaceWall.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, modelCodes, change, accessor]
        );

        const handleEditModelCrawlWallCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "ponyWallInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Pony Wall",
                    isEditing: true,
                    componentType: "PonyWall",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.wall.ponyWall.ponyWallInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibCrawlWallCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "ponyWallInsType";
                const code = { ...libCodes[codeRef], codeRef: codeRef, isLibCode: true };

                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Pony Wall",
                    isEditing: true,
                    componentType: "PonyWall",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.wall.ponyWall.ponyWallInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, libCodes, setInitCode, updateCodeDrawer]
        );

        const gridTemplate = isCompact ? "1fr 1fr 1fr" : isUpgrade ? "2fr 1fr 2fr" : "31.85% 31.85% 23.25%";

        return (
            <>
                {!isUpgrade && (
                    <>
                        <h5 style={{ marginBottom: "0.5rem" }}>Pony Wall</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr" compact={isCompact}>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.wall.ponyWall.enabled`}
                                label="Enable Pony Wall"
                                type="checkbox"
                                large
                                className={classes.ponyWallEnabled}
                                disabled={!canBeEditedFromParametric}
                                compact={isCompact}
                            />
                        </InputRow>
                    </>
                )}
                {isEnabled ? (
                    <>
                        <InputRow gridTemplate={gridTemplate} compact={isCompact}>
                            {!isUpgrade && (
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.wall.ponyWall.ponyWallHeight`}
                                    label="Pony Wall Height"
                                    placeholder="Enter Value"
                                    validate={ponyHeightValidation}
                                    decimals={getDecimalPlaces("basementPonyWallHeight")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("basementPonyWallHeight", modelUnits),
                                        options: getUnitOptions("length"),
                                        selected: ponyWallHeightUnits,
                                        unitType: "length",
                                        accessor: `${accessor}.wall.ponyWall.ponyWallHeight_u`,
                                    }}
                                    disabled={!isEnabled || !canBeEditedFromParametric}
                                    withEquation
                                    equation={ponyWallHeightEquation}
                                    warning={ponyHeightWarning || ponyWallSubCompAreaWarning}
                                    compact={isCompact}
                                />
                            )}
                            <Field
                                component={Select}
                                type="number"
                                name={`${accessor}.wall.ponyWall.ponyWallInsType`}
                                options={[
                                    {
                                        label: "User Specified",
                                        value: {
                                            codeLabel: "User Specified",
                                            codeRef: "UserSpecified",
                                        },
                                    },
                                    ...crawlWallTypeModelCodesMap(modelCodes, libCodes, handleEditModelCrawlWallCode),
                                    ...crawlWallTypeLibCodesMap(libCodes, handleEditLibCrawlWallCode),
                                ].map((option) => ({
                                    ...option,
                                    disabled: codeRefs.includes(option.value?.codeRef),
                                }))}
                                label="Pony Wall Type"
                                placeholder="Choose Pony Wall Type"
                                searchPlaceholder="Search Pony Wall Codes"
                                search={true}
                                footer={() => (
                                    <CodeFooter
                                        toggleDrawer={(value) =>
                                            updateCodeDrawer({
                                                isOpen: value,
                                                codeName: "Pony Wall",
                                                isEditing: false,
                                                componentType: "PonyWall",
                                                codeType: "S",
                                                fieldAccessor: `${accessor}.wall.ponyWall.ponyWallInsType`,
                                                componentId: id,
                                            })
                                        }
                                        setInitCode={setInitCode}
                                        codeType="ponyWallInsType"
                                    />
                                )}
                                className={classes.ponyWallType}
                                disabled={!isEnabled || !canBeEditedFromParametric}
                                isLoading={isCalculatingRValue}
                                onChange={handleTypeChange}
                                validate={requiredCodeRef}
                                compact={isCompact}
                                warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                            />
                            {selectedCodeRef === "UserSpecified" ? (
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.wall.ponyWall.ponyWallInsType_effRVal`}
                                    label={"Effective R-Value"}
                                    placeholder={0.0}
                                    disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                                    setValue={selectedEffRValue}
                                    validate={ponyWallTypeValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("basementPonyWallType")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("basementPonyWallType", modelUnits),
                                        options: getUnitOptions("thermalResistance"),
                                        selected: rValUnits,
                                        unitType: "thermalResistance",
                                        accessor: `${accessor}.wall.ponyWall.ponyWallInsType_u`,
                                    }}
                                    onChange={(value) => {
                                        if (selectedCodeRef === "UserSpecified") {
                                            change(`${accessor}.wall.ponyWall.ponyWallInsType_nomRVal`, value);
                                        }
                                        if (value !== compositePonyEffRValue) {
                                            change(`${accessor}.wall.ponyWall.ponyWallInsType_composite`, {});
                                        }
                                    }}
                                    // info={"User specified values are assumed to be effective R-values."}
                                    editToggle={() =>
                                        updateCompositeCalc({
                                            open: true,
                                            fieldId: "basementPonyWallType",
                                            fieldAccessor: `${accessor}.wall.ponyWall.ponyWallInsType`,
                                            rValUnits,
                                        })
                                    }
                                    compact={isCompact}
                                />
                            ) : (
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.wall.ponyWall.ponyWallInsType_nomRVal`}
                                    label={"Nominal R-Value"}
                                    placeholder={0.0}
                                    disabled={true}
                                    validate={ponyWallTypeValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("basementPonyWallType")}
                                    change={change}
                                    setValue={selectedNomRValue}
                                    units={{
                                        base: getBaseUnits("basementPonyWallType", modelUnits),
                                        options: getUnitOptions("thermalResistance"),
                                        selected: rValUnits,
                                        unitType: "thermalResistance",
                                        accessor: `${accessor}.wall.ponyWall.ponyWallInsType_u`,
                                    }}
                                    info={
                                        "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                                    }
                                    warning={ponyWallCodeWarning}
                                    isLoading={isCalculatingRValue}
                                    compact={isCompact}
                                />
                            )}
                        </InputRow>
                    </>
                ) : (
                    <></>
                )}
                {isCommunity && (
                    <div className={globalStyles.codeDrawerWarning}>
                        <img src={WarningIcon} alt="Warning" />
                        <p>Pony Wall upgrades are not yet supported for communities.</p>
                    </div>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
    updateCompositeCalc: (updates) => dispatch(updateCompositeCalc(updates)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PonyWall);
